import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/requestInterceptor';
import { toast } from 'react-hot-toast';

/**
 * Custom hook for managing template RACI assignments
 * Mirrors the useRaciAssignments hook but for template entities
 */
export const useTemplateRaciAssignments = (templateId) => {
  const [assignments, setAssignments] = useState({
    project: { id: null, assignments: { R: [], A: [], C: [], I: [] } },
    phases: {},
    steps: {},
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  /**
   * Fetch all template assignments for the project, its phases, and steps
   */
  const fetchAllAssignments = useCallback(async () => {
    if (!templateId) {
      // console.log('No templateId provided, skipping assignment fetch');
      setIsLoading(false);
      setError('No template ID provided');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // console.log(`Fetching template assignments for template ID: ${templateId}`);
      
      // Call the endpoint that returns all assignments for a template
      const response = await api.get(`/api/template-assignments/template/${templateId}/all`);
      
      // Default safe structure for assignments
      const defaultData = {
        project: { id: templateId, assignments: { R: [], A: [], C: [], I: [] } },
        phases: {},
        steps: {}
      };
      
      // Use the API response if available, otherwise use defaults
      const data = response.data || defaultData;
      
      // Set assignments state with the data from API
      setAssignments({
        project: data.project || defaultData.project,
        phases: data.phases || defaultData.phases,
        steps: data.steps || defaultData.steps,
      });
      
      setIsLoading(false);
      
      // console.log('Successfully fetched template assignments');
    } catch (error) {
      console.error('Error fetching all template assignments:', error);
      setError(error.message || 'Failed to load template assignments');
      setIsLoading(false);
    }
  }, [templateId]);
  
  /**
   * Get assignments for a specific entity
   */
  const getAssignments = useCallback((entityType, entityId) => {
    if (!entityType || !entityId) {
      return { R: [], A: [], C: [], I: [] };
    }
    
    if (entityType === 'project' && entityId === templateId) {
      return assignments.project.assignments || { R: [], A: [], C: [], I: [] };
    } else if (entityType === 'phase') {
      return assignments.phases[entityId]?.assignments || { R: [], A: [], C: [], I: [] };
    } else if (entityType === 'step') {
      return assignments.steps[entityId]?.assignments || { R: [], A: [], C: [], I: [] };
    }
    
    return { R: [], A: [], C: [], I: [] };
  }, [assignments, templateId]);
  
  /**
   * Update an assignment (add or remove)
   */
  const updateAssignment = useCallback(async (entityType, entityId, roleType, userId, isAdding) => {
    if (!entityType || !entityId || !roleType || !userId) {
      console.error('Missing required parameters for updateAssignment');
      return false;
    }
    
    try {
      if (isAdding) {
        // Add assignment
        // console.log(`Adding ${roleType} assignment for ${entityType} ${entityId} to user ${userId}`);
        
        const response = await api.post('/api/template-assignments', {
          entityType, entityId, userId, roleType
        });
        
        // Update local state optimistically
        if (response.data && response.data.assignment) {
          const assignment = response.data.assignment;
          
          setAssignments(prev => {
            const newState = JSON.parse(JSON.stringify(prev));
            
            if (entityType === 'project') {
              // Ensure the array exists
              if (!newState.project.assignments[roleType]) {
                newState.project.assignments[roleType] = [];
              }
              
              // Add the new assignment
              newState.project.assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            } else if (entityType === 'phase') {
              // Create phase entry if it doesn't exist
              if (!newState.phases[entityId]) {
                newState.phases[entityId] = { assignments: { R: [], A: [], C: [], I: [] } };
              }
              
              // Ensure the array exists
              if (!newState.phases[entityId].assignments[roleType]) {
                newState.phases[entityId].assignments[roleType] = [];
              }
              
              // Add the new assignment
              newState.phases[entityId].assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            } else if (entityType === 'step') {
              // Create step entry if it doesn't exist
              if (!newState.steps[entityId]) {
                newState.steps[entityId] = { assignments: { R: [], A: [], C: [], I: [] } };
              }
              
              // Ensure the array exists
              if (!newState.steps[entityId].assignments[roleType]) {
                newState.steps[entityId].assignments[roleType] = [];
              }
              
              // Add the new assignment
              newState.steps[entityId].assignments[roleType].push({
                ...assignment,
                user_id: assignment.user_id,
                user_name: assignment.user_name || 'Unknown User'
              });
            }
            
            return newState;
          });
        }
        
        return true;
      } else {
        // Remove assignment
        // console.log(`Removing ${roleType} assignment for ${entityType} ${entityId} from user ${userId}`);
        
        // Find existing assignment ID
        const currentAssignments = getAssignments(entityType, entityId);
        
        const existingAssignment = currentAssignments[roleType]?.find(a => 
          String(a.user_id) === String(userId)
        );
        
        if (!existingAssignment) {
          console.error(`Assignment to remove not found for user ${userId} in role ${roleType}`);
          return false;
        }
        
        // Delete the assignment
        await api.delete(`/api/template-assignments/${existingAssignment.id}`);
        
        // Update local state optimistically
        setAssignments(prev => {
          const newState = JSON.parse(JSON.stringify(prev));
          
          if (entityType === 'project') {
            newState.project.assignments[roleType] = 
              newState.project.assignments[roleType].filter(a => 
                String(a.user_id) !== String(userId)
              );
          } else if (entityType === 'phase' && newState.phases[entityId]) {
            newState.phases[entityId].assignments[roleType] = 
              newState.phases[entityId].assignments[roleType].filter(a => 
                String(a.user_id) !== String(userId)
              );
          } else if (entityType === 'step' && newState.steps[entityId]) {
            newState.steps[entityId].assignments[roleType] = 
              newState.steps[entityId].assignments[roleType].filter(a => 
                String(a.user_id) !== String(userId)
              );
          }
          
          return newState;
        });
        
        return true;
      }
    } catch (error) {
      console.error('Error updating template assignment:', error);
      toast.error('Failed to update assignment');
      return false;
    }
  }, [getAssignments]);
  
  /**
   * Check if a user is assigned to a specific RACI role for an entity
   */
  const isUserAssigned = useCallback((userId, entityType, entityId, roleType) => {
    const entityAssignments = getAssignments(entityType, entityId);
    
    if (!entityAssignments || !entityAssignments[roleType]) {
      return false;
    }
    
    return entityAssignments[roleType].some(a => String(a.user_id) === String(userId));
  }, [getAssignments]);
  
  // Initialize by fetching all assignments when the hook mounts or templateId changes
  useEffect(() => {
    if (templateId) {
      fetchAllAssignments();
    } else {
      setIsLoading(false);
    }
  }, [templateId, fetchAllAssignments]);
  
  return {
    // Assignment data access
    templateAssignments: assignments.project?.assignments || { R: [], A: [], C: [], I: [] },
    phases: assignments.phases || {},
    steps: assignments.steps || {},
    getAssignments,
    
    // Assignment operations
    updateAssignment,
    isUserAssigned,
    fetchAllAssignments,
    
    // Status information
    isLoading,
    error
  };
};

export default useTemplateRaciAssignments; 